import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { awardPoints } from '../lib/pointsService';
import { useAuth } from '../context/AuthContext';

// Serialize lesson object into readable text for the AI
function buildLessonText(lesson) {
  const parts = [];
  if (lesson.title)   parts.push(`Title: ${lesson.title}`);
  if (lesson.summary) parts.push(`Summary: ${lesson.summary}`);
  if (lesson.keyPoints?.length)
    parts.push(`Key Points: ${lesson.keyPoints.join('; ')}`);
  if (lesson.content?.length)
    lesson.content.forEach(s => s.heading && parts.push(`${s.heading}: ${s.body || ''}`));
  if (lesson.practicalExample)
    parts.push(`Practical Example: ${lesson.practicalExample}`);
  if (lesson.takeaways?.length)
    parts.push(`Takeaways: ${lesson.takeaways.join('; ')}`);
  return parts.join('\n\n');
}

const OPTION_LABELS = ['A', 'B', 'C', 'D'];

export default function QuizModal({ lesson, subject, level = 'beginner', onClose, onComplete }) {
  const { setTotalPoints, setPointsTier } = useAuth();
  const [phase,       setPhase]       = useState('loading'); // loading | quiz | score
  const [questions,   setQuestions]   = useState([]);
  const [currentIdx,  setCurrentIdx]  = useState(0);
  const [selected,    setSelected]    = useState(null);   // chosen option key e.g. 'A'
  const [revealed,    setRevealed]    = useState(false);  // show correct/wrong
  const [answers,     setAnswers]     = useState([]);     // {correct: bool}[]
  const [error,       setError]       = useState('');

  const generateQuiz = useCallback(async () => {
    setPhase('loading');
    setError('');
    try {
      const lessonContent = buildLessonText(lesson);
      const res = await fetch('/api/generate-quiz', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ lessonContent, subject, level }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to generate quiz');
      setQuestions(data.questions || []);
      setPhase('quiz');
    } catch (err) {
      setError(err.message);
      setPhase('error');
    }
  }, [lesson, subject, level]);

  useEffect(() => { generateQuiz(); }, [generateQuiz]);

  // Save result to Supabase and award points
  const saveResult = useCallback(async (score, total) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return;

    // Save quiz result
    await supabase.from('quiz_results').insert({
      user_id: session.user.id,
      subject,
      score,
      total,
    });

    // Award points based on score
    const pct    = total > 0 ? score / total : 0;
    const bonus  = pct === 1 ? 20 : pct >= 0.8 ? 10 : 0;
    const action = pct === 1 ? 'quiz_100pct' : pct >= 0.8 ? 'quiz_80pct' : 'quiz_complete';
    const pts    = 5 + bonus;

    const result = await awardPoints({
      action,
      points:      pts,
      description: `Quiz on "${subject}": ${score}/${total} correct`,
    });
    if (result?.awarded) {
      setTotalPoints(result.totalPoints);
      setPointsTier(result.tier);
    }
  }, [subject, setTotalPoints, setPointsTier]);

  function handleSelect(optionKey) {
    if (revealed) return;
    setSelected(optionKey);
    setRevealed(true);
  }

  function handleNext() {
    const q       = questions[currentIdx];
    const correct = selected === q.correct;
    const newAnswers = [...answers, { correct }];
    setAnswers(newAnswers);
    setSelected(null);
    setRevealed(false);

    if (currentIdx + 1 >= questions.length) {
      // Quiz complete
      const score = newAnswers.filter(a => a.correct).length;
      saveResult(score, questions.length);
      if (onComplete) onComplete({ score, total: questions.length });
      setPhase('score');
    } else {
      setCurrentIdx(currentIdx + 1);
    }
  }

  const totalQuestions = questions.length;
  const score = answers.filter(a => a.correct).length;

  // ── Render helpers ──────────────────────────────────────────────────────────

  function renderLoading() {
    return (
      <div className="quiz-loading">
        <div className="quiz-spinner" />
        <p>Generating quiz questions…</p>
      </div>
    );
  }

  function renderError() {
    return (
      <div className="quiz-error-state">
        <p>⚠️ {error}</p>
        <button className="quiz-btn quiz-btn--primary" onClick={generateQuiz}>Try Again</button>
      </div>
    );
  }

  function renderQuestion() {
    const q   = questions[currentIdx];
    const pct = ((currentIdx) / totalQuestions) * 100;

    return (
      <>
        {/* Progress */}
        <div className="quiz-progress">
          <div className="quiz-progress-bar">
            <div className="quiz-progress-fill" style={{ width: `${pct}%` }} />
          </div>
          <span className="quiz-progress-label">
            Question {currentIdx + 1} of {totalQuestions}
          </span>
        </div>

        {/* Score running total */}
        <div className="quiz-running-score">
          ✅ {answers.filter(a => a.correct).length} / {answers.length} correct so far
        </div>

        {/* Question */}
        <div className="quiz-question">
          <p className="quiz-question-text">{q.question}</p>
        </div>

        {/* Options */}
        <div className="quiz-options">
          {OPTION_LABELS.map((label) => {
            const text    = q.options[label];
            if (!text) return null;
            const isRight = label === q.correct;
            const isPicked = label === selected;
            let cls = 'quiz-option';
            if (revealed) {
              if (isRight)         cls += ' quiz-option--correct';
              else if (isPicked)   cls += ' quiz-option--wrong';
              else                 cls += ' quiz-option--dim';
            } else if (isPicked) {
              cls += ' quiz-option--selected';
            }
            return (
              <button
                key={label}
                className={cls}
                onClick={() => handleSelect(label)}
                disabled={revealed}
              >
                <span className="quiz-option-label">{label}</span>
                <span className="quiz-option-text">{text}</span>
              </button>
            );
          })}
        </div>

        {/* Explanation after reveal */}
        {revealed && (
          <div className={`quiz-explanation ${selected === q.correct ? 'quiz-explanation--correct' : 'quiz-explanation--wrong'}`}>
            <span className="quiz-explanation-icon">
              {selected === q.correct ? '✅' : '❌'}
            </span>
            <p>
              <strong>{selected === q.correct ? 'Correct!' : `Incorrect — the answer is ${q.correct}.`}</strong>{' '}
              {q.explanation}
            </p>
          </div>
        )}

        {/* Next button — only shown after answering */}
        {revealed && (
          <button className="quiz-btn quiz-btn--primary quiz-btn--next" onClick={handleNext}>
            {currentIdx + 1 >= totalQuestions ? 'See Results' : 'Next Question →'}
          </button>
        )}
      </>
    );
  }

  function renderScore() {
    const pct   = Math.round((score / totalQuestions) * 100);
    const emoji = pct === 100 ? '🏆' : pct >= 80 ? '🌟' : pct >= 60 ? '👍' : '📚';
    const msg   = pct === 100 ? 'Perfect score! Outstanding!'
                : pct >= 80  ? 'Great job! You really know this topic.'
                : pct >= 60  ? 'Good effort! Review the lesson to strengthen your knowledge.'
                :              'Keep studying — revisit the lesson and try again!';
    return (
      <div className="quiz-score-screen">
        <div className="quiz-score-emoji">{emoji}</div>
        <h3 className="quiz-score-title">Quiz Complete!</h3>
        <div className="quiz-score-ring">
          <span className="quiz-score-number">{score}/{totalQuestions}</span>
          <span className="quiz-score-pct">{pct}%</span>
        </div>
        <p className="quiz-score-msg">{msg}</p>
        <div className="quiz-score-breakdown">
          {answers.map((a, i) => (
            <span key={i} className={`quiz-dot ${a.correct ? 'quiz-dot--correct' : 'quiz-dot--wrong'}`} />
          ))}
        </div>
        <div className="quiz-score-actions">
          <button className="quiz-btn quiz-btn--secondary" onClick={generateQuiz}>
            🔄 Retake Quiz
          </button>
          <button className="quiz-btn quiz-btn--primary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-backdrop quiz-backdrop" onClick={onClose}>
      <div className="quiz-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose} aria-label="Close">✕</button>

        <div className="quiz-header">
          <h2 className="quiz-title">🧠 Quiz: {subject}</h2>
          {phase === 'quiz' && (
            <p className="quiz-subtitle">Test your understanding of the lesson</p>
          )}
        </div>

        <div className="quiz-body">
          {phase === 'loading' && renderLoading()}
          {phase === 'error'   && renderError()}
          {phase === 'quiz'    && questions.length > 0 && renderQuestion()}
          {phase === 'score'   && renderScore()}
        </div>
      </div>
    </div>
  );
}
